/**
 * Gestion des quotas TeckiA Video Ads pour Admin
 * ✅ Header fixe, tableau scrollable, pagination 50
 * ✅ Badge PRO/FREE + bouton toggle pricing_type
 */

import React, { useState, useEffect } from 'react';
import {
  Video, Users, Search, Edit2, Save, X, AlertCircle,
  CheckCircle, Sparkles, RefreshCw, Package, Crown, Gift
} from 'lucide-react';
import axios from 'axios';
import { API_URL } from '../../config';

const PAGE_SIZE = 50;

export default function VideoAdsQuotas() {
  const [users,       setUsers]       = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [searchTerm,  setSearchTerm]  = useState('');
  const [page,        setPage]        = useState(1);
  const [editingUser, setEditingUser] = useState(null);
  const [editValues,  setEditValues]  = useState({ max_videos_standard: 0, max_videos_grok: 0 });
  const [message,     setMessage]     = useState(null);
  const [stats,       setStats]       = useState({
    total_users: 0, users_with_subscription: 0,
    total_videos_standard: 0, total_videos_grok: 0
  });

  useEffect(() => { loadUsers(); }, []);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/admin/video-ads-quotas`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      });
      if (r.data.success) { setUsers(r.data.users); setStats(r.data.stats); }
    } catch { showMessage('Erreur chargement', 'error'); }
    finally { setLoading(false); }
  };

  const startEdit = (user) => {
    setEditingUser(user.user_id);
    setEditValues({ max_videos_standard: user.max_videos_standard || 0, max_videos_grok: user.max_videos_grok || 0 });
  };
  const cancelEdit = () => { setEditingUser(null); };

  const saveEdit = async (userId) => {
    try {
      const r = await axios.post(`${API_URL}/api/admin/video-ads-quotas/update`,
        { user_id: userId, ...editValues },
        { headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` } }
      );
      if (r.data.success) { showMessage('Quota mis à jour ✅', 'success'); loadUsers(); cancelEdit(); }
    } catch { showMessage('Erreur mise à jour', 'error'); }
  };

  const togglePricingType = async (subscriptionId, currentType) => {
    const newType = currentType === 'pro' ? 'free' : 'pro';
    if (!window.confirm(`Passer en ${newType.toUpperCase()} ?`)) return;
    try {
      const r = await axios.post(`${API_URL}/api/admin/video-ads-quotas/update-pricing-type`,
        { subscription_id: subscriptionId, pricing_type: newType },
        { headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` } }
      );
      if (r.data.success) { showMessage(`Passé en ${newType.toUpperCase()} ✅`, 'success'); loadUsers(); }
    } catch { showMessage('Erreur changement plan', 'error'); }
  };

  const resetUsage = async (userId, videoType) => {
    if (!window.confirm(`Réinitialiser ${videoType} ?`)) return;
    try {
      const r = await axios.post(`${API_URL}/api/admin/video-ads-quotas/reset`,
        { user_id: userId, video_type: videoType },
        { headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` } }
      );
      if (r.data.success) { showMessage('Réinitialisé ✅', 'success'); loadUsers(); }
    } catch { showMessage('Erreur reset', 'error'); }
  };

  const showMessage = (text, type) => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 3000);
  };

  const filtered   = users.filter(u => {
    const s = searchTerm.toLowerCase();
    return u.email?.toLowerCase().includes(s) || u.name?.toLowerCase().includes(s) || u.user_id?.toLowerCase().includes(s);
  });
  const totalPages = Math.ceil(filtered.length / PAGE_SIZE) || 1;
  const paginated  = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  /* ── Layout : height = 100vh - 64px (header mobile) ── */
  return (
    <div className="flex flex-col" style={{ height: 'calc(100vh - 64px)', overflow: 'hidden', padding: '24px 32px 0' }}>

      {/* ══ HEADER FIXE ══ */}
      <div className="flex-shrink-0">

        {/* Titre + bouton */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-white flex items-center gap-3">
              <Video className="w-8 h-8 text-pink-500" />
              Quotas TeckiA Video Ads
            </h1>
            <p className="text-blue-200 text-sm mt-1">Gestion des quotas vidéo par utilisateur</p>
          </div>
          <button onClick={loadUsers} disabled={loading}
            className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-pink-600 to-orange-500 text-white rounded-xl font-semibold disabled:opacity-50 shadow-lg">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Actualiser
          </button>
        </div>

        {/* Message */}
        {message && (
          <div className={`mb-3 p-3 rounded-xl flex items-center gap-2 text-sm ${
            message.type === 'success' ? 'bg-green-500/20 border border-green-500/50 text-green-300' : 'bg-red-500/20 border border-red-500/50 text-red-300'
          }`}>
            {message.type === 'success' ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
            {message.text}
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-4 gap-3 mb-4">
          {[
            { icon: Users,    label: 'Total',         value: stats.total_users,               color: 'from-blue-600 to-cyan-600' },
            { icon: Package,  label: 'Abonnés',       value: stats.users_with_subscription,   color: 'from-green-600 to-emerald-600' },
            { icon: Video,    label: 'Vidéos Std',    value: stats.total_videos_standard,      color: 'from-purple-600 to-pink-600' },
            { icon: Sparkles, label: 'Vidéos Grok',   value: stats.total_videos_grok,          color: 'from-pink-600 to-orange-600' },
          ].map((k, i) => (
            <div key={i} className={`bg-gradient-to-br ${k.color} rounded-xl p-4 text-white flex items-center justify-between`}>
              <div><k.icon className="w-7 h-7 opacity-80 mb-1" /><div className="text-xs font-semibold opacity-80">{k.label}</div></div>
              <div className="text-3xl font-black">{k.value}</div>
            </div>
          ))}
        </div>

        {/* Légende + Search */}
        <div className="flex items-center gap-4 mb-3">
          <div className="flex items-center gap-3 text-xs">
            <span className="flex items-center gap-1 text-yellow-300 font-semibold"><Crown className="w-3.5 h-3.5" />PRO = badge PRO client</span>
            <span className="text-white/20">|</span>
            <span className="flex items-center gap-1 text-blue-300 font-semibold"><Gift className="w-3.5 h-3.5" />FREE = plan gratuit</span>
          </div>
          <div className="relative flex-1 max-w-sm ml-auto">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input value={searchTerm}
              onChange={e => { setSearchTerm(e.target.value); setPage(1); }}
              placeholder="Rechercher…"
              className="w-full pl-9 pr-4 py-2 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-400 text-sm focus:outline-none focus:ring-2 focus:ring-pink-500" />
          </div>
        </div>
      </div>

      {/* ══ TABLEAU SCROLLABLE ══ */}
      <div className="flex-1 min-h-0 overflow-y-auto rounded-2xl border border-white/10" style={{ scrollbarWidth: 'thin' }}>
        <table className="w-full" style={{ borderCollapse: 'separate', borderSpacing: 0 }}>
          <thead>
            <tr style={{ position: 'sticky', top: 0, zIndex: 10, background: 'rgba(15,10,40,0.97)', backdropFilter: 'blur(8px)' }}>
              {['Utilisateur', 'Plan', 'Standard PRO', 'Grok Imagine', 'Total Restant', 'Actions'].map((h, i) => (
                <th key={i} className={`px-5 py-3 text-xs font-black uppercase tracking-widest text-gray-400 border-b border-white/10 ${i === 0 ? 'text-left' : 'text-center'} ${i === 5 ? 'text-right' : ''}`}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {loading && users.length === 0 ? (
              <tr><td colSpan={6} className="py-20 text-center text-gray-400">
                <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-2 text-pink-500" /><p>Chargement…</p>
              </td></tr>
            ) : paginated.length === 0 ? (
              <tr><td colSpan={6} className="py-20 text-center text-gray-400">
                <Video className="w-10 h-10 mx-auto mb-2 opacity-40" /><p>Aucun utilisateur trouvé</p>
              </td></tr>
            ) : paginated.map(user => (
              <tr key={user.user_id} className="hover:bg-white/5 transition">

                {/* User */}
                <td className="px-5 py-3.5">
                  <div className="text-white font-semibold text-sm">{user.name || 'Sans nom'}</div>
                  <div className="text-gray-400 text-xs">{user.email}</div>
                  {user.has_subscription && (
                    <span className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 bg-green-500/20 text-green-300 text-xs rounded-full">
                      <CheckCircle className="w-3 h-3" />Abonné
                    </span>
                  )}
                </td>

                {/* Plan */}
                <td className="px-5 py-3.5 text-center">
                  {user.has_subscription ? (
                    <div className="flex flex-col items-center gap-1.5">
                      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-black ${
                        user.pricing_type === 'pro'
                          ? 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/40'
                          : 'bg-blue-500/20 text-blue-300 border border-blue-500/40'
                      }`}>
                        {user.pricing_type === 'pro' ? <Crown className="w-3 h-3" /> : <Gift className="w-3 h-3" />}
                        {(user.pricing_type || 'free').toUpperCase()}
                      </span>
                      <button onClick={() => togglePricingType(user.subscription_id, user.pricing_type || 'free')}
                        className={`text-xs px-2 py-0.5 rounded-lg font-semibold transition ${
                          user.pricing_type === 'pro'
                            ? 'bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 border border-blue-500/25'
                            : 'bg-yellow-500/10 hover:bg-yellow-500/20 text-yellow-400 border border-yellow-500/25'
                        }`}>
                        {user.pricing_type === 'pro' ? '↓ Free' : '↑ PRO'}
                      </button>
                    </div>
                  ) : <span className="text-gray-600 text-xs">—</span>}
                </td>

                {/* Standard */}
                <td className="px-5 py-3.5">
                  {editingUser === user.user_id ? (
                    <div className="flex flex-col items-center gap-1">
                      <label className="text-xs text-gray-400">Max</label>
                      <input type="number" value={editValues.max_videos_standard}
                        onChange={e => setEditValues({...editValues, max_videos_standard: parseInt(e.target.value)||0})}
                        className="w-20 px-2 py-1 bg-white/10 border border-white/20 rounded text-white text-sm text-center" />
                      <span className="text-xs text-gray-500">Utilisé: {user.used_videos_standard}</span>
                    </div>
                  ) : (
                    <div className="text-center">
                      <div className="text-white font-bold">{user.remaining_standard||0} / {user.max_videos_standard||0}</div>
                      <div className="text-xs text-gray-400 mb-1">Utilisé: {user.used_videos_standard||0}</div>
                      <div className="w-full bg-gray-700 rounded-full h-1.5">
                        <div className="bg-gradient-to-r from-purple-500 to-pink-500 h-1.5 rounded-full"
                          style={{ width: `${user.max_videos_standard > 0 ? ((user.remaining_standard||0)/user.max_videos_standard)*100 : 0}%` }} />
                      </div>
                    </div>
                  )}
                </td>

                {/* Grok */}
                <td className="px-5 py-3.5">
                  {editingUser === user.user_id ? (
                    <div className="flex flex-col items-center gap-1">
                      <label className="text-xs text-gray-400">Max</label>
                      <input type="number" value={editValues.max_videos_grok}
                        onChange={e => setEditValues({...editValues, max_videos_grok: parseInt(e.target.value)||0})}
                        className="w-20 px-2 py-1 bg-white/10 border border-white/20 rounded text-white text-sm text-center" />
                      <span className="text-xs text-gray-500">Utilisé: {user.used_videos_grok}</span>
                    </div>
                  ) : (
                    <div className="text-center">
                      <div className="text-white font-bold flex items-center justify-center gap-1">
                        <Sparkles className="w-3.5 h-3.5 text-pink-400" />
                        {user.remaining_grok||0} / {user.max_videos_grok||0}
                      </div>
                      <div className="text-xs text-gray-400 mb-1">Utilisé: {user.used_videos_grok||0}</div>
                      <div className="w-full bg-gray-700 rounded-full h-1.5">
                        <div className="bg-gradient-to-r from-pink-500 to-orange-500 h-1.5 rounded-full"
                          style={{ width: `${user.max_videos_grok > 0 ? ((user.remaining_grok||0)/user.max_videos_grok)*100 : 0}%` }} />
                      </div>
                    </div>
                  )}
                </td>

                {/* Total */}
                <td className="px-5 py-3.5 text-center">
                  <div className="text-xl font-black text-white">{(user.remaining_standard||0)+(user.remaining_grok||0)}</div>
                  <div className="text-xs text-gray-400">restantes</div>
                </td>

                {/* Actions */}
                <td className="px-5 py-3.5">
                  <div className="flex items-center justify-end gap-1.5">
                    {editingUser === user.user_id ? (
                      <>
                        <button onClick={() => saveEdit(user.user_id)} className="p-2 bg-green-500/20 hover:bg-green-500/30 text-green-300 rounded-lg transition" title="Sauvegarder"><Save className="w-4 h-4" /></button>
                        <button onClick={cancelEdit} className="p-2 bg-red-500/20 hover:bg-red-500/30 text-red-300 rounded-lg transition" title="Annuler"><X className="w-4 h-4" /></button>
                      </>
                    ) : (
                      <>
                        <button onClick={() => startEdit(user)} className="p-2 bg-blue-500/20 hover:bg-blue-500/30 text-blue-300 rounded-lg transition" title="Modifier"><Edit2 className="w-4 h-4" /></button>
                        <button onClick={() => resetUsage(user.user_id, 'standard')} className="p-2 bg-purple-500/20 hover:bg-purple-500/30 text-purple-300 rounded-lg transition" title="Reset Standard"><RefreshCw className="w-4 h-4" /></button>
                        <button onClick={() => resetUsage(user.user_id, 'grok')} className="p-2 bg-pink-500/20 hover:bg-pink-500/30 text-pink-300 rounded-lg transition" title="Reset Grok"><Sparkles className="w-4 h-4" /></button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ══ PAGINATION + résumé ══ */}
      <div className="flex-shrink-0 flex items-center justify-between py-3">
        <span className="text-gray-400 text-sm">{filtered.length} utilisateurs · page {page}/{totalPages}</span>
        {totalPages > 1 && (
          <div className="flex items-center gap-1.5">
            <button onClick={() => setPage(p => Math.max(1, p-1))} disabled={page === 1}
              className="px-3 py-1.5 rounded-lg text-sm font-semibold disabled:opacity-30 bg-white/10 hover:bg-white/15 text-white transition">← Préc.</button>
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
              const p = totalPages <= 5 ? i+1 : page <= 3 ? i+1 : page >= totalPages-2 ? totalPages-4+i : page-2+i;
              return <button key={p} onClick={() => setPage(p)}
                className={`px-3 py-1.5 rounded-lg text-sm font-bold transition ${p === page ? 'bg-pink-600 text-white shadow' : 'bg-white/10 hover:bg-white/15 text-gray-300'}`}>{p}</button>;
            })}
            <button onClick={() => setPage(p => Math.min(totalPages, p+1))} disabled={page >= totalPages}
              className="px-3 py-1.5 rounded-lg text-sm font-semibold disabled:opacity-30 bg-white/10 hover:bg-white/15 text-white transition">Suiv. →</button>
          </div>
        )}
      </div>
    </div>
  );
}